import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Layers, Battery, Gauge, Sparkles, Zap, MessageSquare } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import RobotRecommendationWidget from "@/components/RobotRecommendationWidget";
import RobotPageNav from "@/components/RobotPageNav";
import { ProductPageQuoteForm } from "@/components/ProductPageQuoteForm";
import { SpecSheetDownload } from "@/components/SpecSheetDownload";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import DesktopStickyCTA from "@/components/DesktopStickyCTA";
import bellabotImg from "@/assets/robots/bellabot-hero.png";

const BellaBot = () => {
  const specifications = [
    {
      icon: <Layers className="w-5 h-5" />,
      label: "Tray Capacity",
      value: "4 Smart Trays"
    },
    {
      icon: <Gauge className="w-5 h-5" />,
      label: "Mobility",
      value: "65cm Clearance"
    },
    {
      icon: <Battery className="w-5 h-5" />,
      label: "Battery",
      value: "Swappable Technology"
    },
    {
      icon: <MessageSquare className="w-5 h-5" />,
      label: "Interaction",
      value: "Multi-Modal"
    }
  ];

  const keyFeatures = [
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Multi-Modal Interaction",
      description: "Engage customers with voice feedback, LED expressions, and interactive animations that create memorable dining experiences and delight guests of all ages."
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Smart Tray Detection",
      description: "Advanced sensors automatically detect when items are placed or removed from trays, ensuring accurate delivery tracking and timely customer notifications."
    },
    {
      icon: <Gauge className="w-6 h-6" />,
      title: "65cm Superior Mobility",
      description: "Navigate through crowded restaurants and tight spaces with ease thanks to the compact design requiring only 65cm minimum passage width."
    },
    {
      icon: <Battery className="w-6 h-6" />,
      title: "Battery Swapping Technology",
      description: "Industry-first hot-swappable battery system enables continuous operation without downtime, perfect for busy service periods."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Efficient Delivery",
      description: "Optimize service workflow with autonomous navigation, automatic obstacle avoidance, and precise table positioning for reliable delivery performance."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Premium Design",
      description: "Elegant cat-themed design with LED expressions and smooth movements that enhance your restaurant's ambiance and customer experience."
    }
  ];

  const applications = [
    {
      title: "Full-Service Restaurants",
      description: "Food and beverage delivery from kitchen to tables, dirty dish collection, and improved table turnover rates while reducing server workload."
    },
    {
      title: "Quick Service Restaurants",
      description: "Fast and efficient order delivery to tables, queue management, and enhanced customer satisfaction during peak hours."
    },
    {
      title: "Hotels & Resorts",
      description: "Room service delivery, amenity distribution, and guest engagement to elevate hospitality service standards."
    },
    {
      title: "Cafés & Coffee Shops",
      description: "Beverage and pastry delivery, table clearing, and maintaining efficient operations during busy periods."
    },
    {
      title: "Buffets & Banquet Halls",
      description: "Dish replenishment, dirty dish collection, and service enhancement for large-scale dining operations."
    },
    {
      title: "Entertainment Venues",
      description: "Concession delivery and service enhancement in theaters, arcades, and entertainment centers to improve guest satisfaction."
    }
  ];

  return (
    <>
      <SEO
        title="BellaBot - Premium Delivery Robot | Neil AI"
        description="Premium delivery robot with multi-modal interaction, smart tray detection, and industry-first battery swapping technology. Enhance your restaurant service with BellaBot."
        keywords="BellaBot, delivery robot, restaurant robot, smart tray detection, multi-modal interaction, battery swapping"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <RobotRecommendationWidget />
        
        <main className="container mx-auto px-4 pt-12 pb-8 md:pt-16 md:pb-12">
          <RobotPageNav />

          {/* Hero Section */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-12">
            <div className="space-y-6">
              <div>
                <Badge className="mb-4">Premium Delivery Robot</Badge>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">
                  BellaBot
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                  Premium delivery robot with multi-modal interaction and smart tray detection designed to enhance customer experience and operational efficiency.
                </p>
              </div>

              {/* Quick Specs */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {specifications.map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <span className="text-xs font-semibold">{spec.label}</span>
                      </div>
                      <p className="text-xs md:text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <Button asChild size="lg">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Schedule Demo
                  </a>
                </Button>
                <ProductPageQuoteForm robotModel="BellaBot" />
                <SpecSheetDownload robotModel="BellaBot" />
                <Button asChild variant="outline" size="lg">
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
              </div>

              {/* ROI Highlight */}
              <Card className="glass-effect border-primary/30 bg-primary/5">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold mb-1 text-sm md:text-base">ROI Impact</h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Reduce labor costs by up to 50% while improving service speed and customer satisfaction. Typical payback period of 8-12 months.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Product Image */}
            <div className="relative">
              <div className="aspect-square md:aspect-auto md:h-full rounded-lg overflow-hidden bg-black flex items-center justify-center p-4 md:p-8">
                <img 
                  src={bellabotImg} 
                  alt="BellaBot - Premium Delivery Robot"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>

          {/* Detailed Information Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="specs">Specifications</TabsTrigger>
              <TabsTrigger value="applications">Applications</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Key Features</CardTitle>
                  <CardDescription>
                    Premium features designed for exceptional service delivery
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {keyFeatures.map((feature, idx) => (
                      <div key={idx} className="flex gap-3 md:gap-4">
                        <div className="text-primary flex-shrink-0">{feature.icon}</div>
                        <div>
                          <h4 className="font-bold mb-1 md:mb-2 text-sm md:text-base">{feature.title}</h4>
                          <p className="text-xs md:text-sm text-muted-foreground">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specs" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Technical Specifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 md:space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Tray Capacity</span>
                      <span className="text-sm md:text-base">4 Smart Trays with Detection</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Minimum Passage Width</span>
                      <span className="text-sm md:text-base">65cm Superior Mobility</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Navigation</span>
                      <span className="text-sm md:text-base">VSLAM+ Positioning</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Battery Technology</span>
                      <span className="text-sm md:text-base">Industry-First Swappable Battery</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Interaction</span>
                      <span className="text-sm md:text-base">Multi-Modal (Voice, LED, Animation)</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Safety Features</span>
                      <span className="text-sm md:text-base">360° Obstacle Detection</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                      <span className="font-semibold text-sm md:text-base">Deployment</span>
                      <span className="text-sm md:text-base">Marker-less VSLAM+</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Ideal Applications</CardTitle>
                  <CardDescription>
                    Perfect for various hospitality and service environments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="p-3 md:p-4 rounded-lg border border-[hsl(var(--line))] hover:border-primary/50 transition-colors">
                        <h4 className="font-bold mb-2 text-sm md:text-base">{app.title}</h4>
                        <p className="text-xs md:text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Final CTA */}
          <Card className="glass-effect border-primary/30 bg-primary/5 mt-8 md:mt-12">
            <CardContent className="p-6 md:p-8 text-center">
              <h2 className="font-orbitron font-bold text-xl md:text-2xl mb-4">
                Ready to Enhance Your Service Experience?
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-2xl mx-auto">
                Transform your restaurant operations with BellaBot's premium delivery capabilities and delightful customer interactions.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Button asChild size="lg">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Schedule a Demo
                  </a>
                </Button>
                <Button asChild variant="secondary" size="lg">
                  <Link to="/robot-recommender">
                    Find Your Perfect Robot
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>

        <MobileStickyCTA />
        <DesktopStickyCTA />
        <Footer />
      </div>
    </>
  );
};

export default BellaBot;
